import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import {
  Input,
  InputNumber,
  Table,
  Button,
  Space,
  message,
  Upload,
  Modal,
  Form,
  Radio,
  Select,
  Tooltip,
  Typography
} from 'antd'
import { UploadOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { getMaterials } from '../calculators/materials'
import advantageMaterials from '../data/advantage/materials'
import integrityMaterials from '../data/integrity/materials'
import { getParts } from '../calculators/parts'
import advantageParts from '../data/advantage/parts'
import integrityParts from '../data/integrity/parts'
import { getManufacturedPartsDataset, getManufacturedPartCalculations } from '../calculators/manufactured-parts'
import { convertStringToTitleCase, generateTableColumns, handleSearch } from '../helpers'
import { clearPricingConfigCache, fetchPricingConfig } from '../utils/pricingConfig'

const { Search } = Input
const { Text } = Typography

// A material price field is editable only if it's hardcoded in the JSON AND
// the other price field is not hardcoded (mutual exclusion: pricePerPound xor pricePerUnit)
function isMaterialFieldEditable(rawItem, field) {
  if (!rawItem) return false
  const val = rawItem[field]
  if (val === null || val === false || val === undefined) return false
  if (field === 'pricePerPound') {
    const otherVal = rawItem.pricePerUnit
    return otherVal === null || otherVal === false || otherVal === undefined
  }
  if (field === 'pricePerUnit') {
    const otherVal = rawItem.pricePerPound
    return otherVal === null || otherVal === false || otherVal === undefined
  }
  return true
}

const materialsColumns = ['description', 'partNumber', 'unitType', 'unitDivisor', 'pricePerPound', 'pricePerUnit', 'poundsPerStock', 'poundsPerUnit']
const partsColumns = ['description', 'partNumber', 'weight', 'galv', 'steelMrk', 'steelDrop', 'steelCost', 'fastener', 'cover', 'labor', 'totalMrk']
const initialPartsMaterialsColumns = ['id', 'weightMultiplier', 'steelDropMultiplier', 'steelCostMultiplier', 'fastenerMultiplier', 'coverMultiplier']

// Fields that are editable per data type
const EDITABLE_MATERIAL_FIELDS = ['pricePerPound', 'pricePerUnit']
const EDITABLE_PART_FIELDS = ['fastener', 'cover']

function EditableCell({ value, draftValue, publishedValue, field, item, buildingType, dataType, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [inputVal, setInputVal] = useState(draftValue !== null && draftValue !== undefined ? draftValue : value)
  const [saving, setSaving] = useState(false)
  const priceChangeAnimated = React.useRef(false)

  // Sync inputVal when draft changes externally (e.g. after save refreshes sibling field)
  useEffect(() => {
    if (!editing) {
      setInputVal(draftValue !== null && draftValue !== undefined ? draftValue : value)
    }
  }, [draftValue, value])

  const isPartField = dataType === 'parts'
  if (value === null || value === false || value === undefined) {
    return <span style={{ color: '#C0C0C0' }}>{value === false ? 'false' : 'null'}</span>
  }

  const fmt = v => typeof v === 'number' ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : (v ?? 'null')

  // Previous price is always the published JSON baseline
  const prevPrice = publishedValue !== null && publishedValue !== undefined ? publishedValue : null

  const PriceChange = ({ compareValue }) => {
    if (prevPrice === null || compareValue === null || compareValue === undefined || compareValue === prevPrice) return null
    const pct = ((compareValue - prevPrice) / prevPrice) * 100
    const sign = pct > 0 ? '+' : ''
    const isFirstTime = !priceChangeAnimated.current
    if (isFirstTime) priceChangeAnimated.current = true
    return (
      <div style={{
        marginTop: 6, fontSize: 11,
        overflow: 'hidden',
        maxHeight: 50,
        opacity: 1,
        animation: isFirstTime ? 'priceChangeIn 0.25s ease' : 'none',
      }}>
        <div style={{ borderTop: '1px solid #e8e8e8', marginBottom: 6 }} />
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#888' }}>Previous Price</span>
            <strong style={{ color: '#333', fontVariantNumeric: 'tabular-nums' }}>{fmt(prevPrice)}</strong>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: pct > 0 ? '#23672A' : '#9C1937' }}>Change</span>
            <span style={{ color: pct > 0 ? '#23672A' : '#9C1937', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{sign}{pct.toFixed(2)}%</span>
          </div>
        </div>
      </div>
    )
  }

  const save = async () => {
    if (inputVal === value) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const endpoint = isPartField
        ? `/api/pricing/config/${buildingType}/part`
        : `/api/pricing/config/${buildingType}/material`

      const body = isPartField
        ? { partId: item.id, [field]: parseFloat(inputVal) }
        : { materialId: item.id, [field]: parseFloat(inputVal) }

      await axios.put(endpoint, body, { withCredentials: true })
      clearPricingConfigCache()
      message.success('Saved to draft')
      onSaved()
      setEditing(false)
    } catch (err) {
      console.error(err)
      message.error('Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    const resetTo = draftValue !== null && draftValue !== undefined ? draftValue : value
    setInputVal(resetTo)
    setEditing(false)
    // If there's no draft (no persistent change shown in view mode), reset so animation plays next time
    if (draftValue === null || draftValue === undefined) {
      priceChangeAnimated.current = false
    }
  }

  if (editing) {
    return (
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <InputNumber
            size="small"
            value={inputVal}
            onChange={setInputVal}
            step={0.01}
            precision={4}
            style={{ width: 80 }}
            autoFocus
            onPressEnter={save}
          />
          <Button size="small" icon={<CheckOutlined />} onClick={save} loading={saving} type="primary" style={{ backgroundColor: '#7b2d3a', borderColor: '#7b2d3a' }} />
          <Button size="small" icon={<CloseOutlined />} onClick={cancel} style={{ backgroundColor: '#555', borderColor: '#555', color: '#fff' }} />
        </div>
        <PriceChange compareValue={inputVal} />
      </div>
    )
  }

  return (
    <Tooltip title="Click to edit">
      <div onClick={() => { setInputVal(draftValue !== null && draftValue !== undefined ? draftValue : value); setEditing(true) }} style={{ cursor: 'pointer' }}>
        <span style={{ fontWeight: 500 }}>
          {draftValue !== null && draftValue !== undefined ? fmt(draftValue) : fmt(value)}
        </span>
        <EditOutlined style={{ marginLeft: 6, fontSize: 11, color: '#aaa' }} />
        <PriceChange compareValue={draftValue !== null && draftValue !== undefined ? draftValue : value} />
      </div>
    </Tooltip>
  )
}

function BulkUpdateModal({ open, onClose, buildingType, compareData, liveData, onSaved }) {
  const [fromPrice, setFromPrice] = useState(null)
  const [toPrice, setToPrice] = useState(null)
  const [saving, setSaving] = useState(false)

  // Unique prices from live data (reflects published DB values), filtered to only
  // hardcoded rows (those that have a pricePerPound in the raw JSON)
  const uniquePrices = useMemo(() => {
    const prices = new Set()
    liveData.forEach(item => {
      const raw = compareData.find(r => r.id === item.id)
      if (isMaterialFieldEditable(raw, 'pricePerPound') && item.pricePerPound !== null && item.pricePerPound !== undefined) {
        prices.add(item.pricePerPound)
      }
    })
    return [...prices].sort((a, b) => a - b)
  }, [liveData, compareData])

  const affected = useMemo(() => {
    if (fromPrice === null) return []
    return liveData.filter(item => {
      const raw = compareData.find(r => r.id === item.id)
      return isMaterialFieldEditable(raw, 'pricePerPound') && item.pricePerPound === fromPrice
    })
  }, [fromPrice, liveData, compareData])

  const handleSave = async () => {
    if (fromPrice === null || toPrice === null) return
    if (fromPrice === toPrice) { onClose(); return }
    setSaving(true)
    try {
      for (const item of affected) {
        await axios.put(`/api/pricing/config/${buildingType}/material`, {
          materialId: item.id,
          pricePerPound: toPrice
        }, { withCredentials: true })
      }
      clearPricingConfigCache()
      message.success(`Updated ${affected.length} material${affected.length !== 1 ? 's' : ''} to $${toPrice}/lb`)
      onSaved()
      onClose()
      setFromPrice(null)
      setToPrice(null)
    } catch {
      message.error('Failed to bulk update')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Bulk Update Price / lb"
      open={open}
      onCancel={() => { onClose(); setFromPrice(null); setToPrice(null) }}
      onOk={handleSave}
      okText={affected.length ? `Update ${affected.length} material${affected.length !== 1 ? 's' : ''}` : 'Update'}
      okButtonProps={{ disabled: fromPrice === null || toPrice === null || toPrice === fromPrice, loading: saving }}
      width={440}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>Change all materials where Price / lb is</Text>
          <Select
            placeholder="Select current price"
            value={fromPrice}
            onChange={val => { setFromPrice(val); setToPrice(null) }}
            style={{ width: '100%' }}
            options={uniquePrices.map(p => ({ value: p, label: `$${p}` }))}
          />
        </div>

        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>New Price / lb</Text>
          <InputNumber
            value={toPrice}
            onChange={setToPrice}
            step={0.01}
            precision={4}
            prefix="$"
            style={{ width: '100%' }}
            disabled={fromPrice === null}
          />
        </div>

        {fromPrice !== null && (
          affected.length ? (
            <div>
              <Text type="secondary"><strong>{affected.length}</strong> material{affected.length !== 1 ? 's' : ''} will be updated:</Text>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {affected.map(m => <li key={m.id}><Text type="secondary">{m.description}</Text></li>)}
              </ul>
            </div>
          ) : (
            <Text type="warning">No materials match that price.</Text>
          )
        )}
      </Space>
    </Modal>
  )
}

export default function PartsAndMaterialsTable (props) {
  const allCompareData = { advantageMaterials, integrityMaterials, advantageParts, integrityParts }
  const compareData = allCompareData[props.buildingType + convertStringToTitleCase(props.dataType)]
  const initialColumns = props.dataType === 'materials' ? materialsColumns : partsColumns
  const [pricingConfig, setPricingConfig] = useState(null)
  const [data, setData] = useState([])
  const [tableLoading, setTableLoading] = useState(false)
  const [pdfUrls, setPdfUrls] = useState({})
  const [modalVisible, setModalVisible] = useState(false)
  const [currentItem, setCurrentItem] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)
  const [pdfForm] = Form.useForm()
  const pdfType = Form.useWatch('type', pdfForm)

  const editableFields = props.dataType === 'materials' ? EDITABLE_MATERIAL_FIELDS : EDITABLE_PART_FIELDS

  const applyDraftToConfig = useCallback((config, draft) => {
    if (!draft || !config) return config
    const patched = JSON.parse(JSON.stringify(config))
    if (draft.materials?.length) {
      draft.materials.forEach(dm => {
        const m = patched.materials?.find(m => m.materialId === dm.materialId)
        if (m) {
          if (dm.pricePerPound !== null && dm.pricePerPound !== undefined) m.pricePerPound = dm.pricePerPound
          if (dm.pricePerUnit !== null && dm.pricePerUnit !== undefined) m.pricePerUnit = dm.pricePerUnit
        }
      })
    }
    if (draft.parts?.length) {
      draft.parts.forEach(dp => {
        const p = patched.parts?.find(p => p.partId === dp.partId)
        if (p) {
          if (dp.fastener !== null && dp.fastener !== undefined) p.fastener = dp.fastener
          if (dp.cover !== null && dp.cover !== undefined) p.cover = dp.cover
        }
      })
    }
    return patched
  }, [])

  const loadData = useCallback((config, draft) => {
    const effectiveConfig = applyDraftToConfig(config, draft ?? null)
    const freshData = props.dataType === 'materials'
      ? getMaterials(null, props.buildingType, effectiveConfig)
      : getParts(null, props.buildingType, undefined, effectiveConfig)
    setData(freshData)
  }, [props.buildingType, props.dataType, applyDraftToConfig])

  const refreshData = useCallback(() => {
    clearPricingConfigCache()
    Promise.all([
      fetchPricingConfig(props.buildingType),
      axios.get(`/api/pricing/draft/${props.buildingType}`, { withCredentials: true }).then(r => r.data).catch(() => null)
    ]).then(([config, freshDraft]) => {
      setPricingConfig(config)
      loadData(config, freshDraft)
    })
  }, [props.buildingType, loadData])

  useEffect(() => {
    setData([])
    setTableLoading(true)
    clearPricingConfigCache()
    fetchPricingConfig(props.buildingType).then(config => {
      setPricingConfig(config)
      loadData(config)
      setTableLoading(false)
    })
  }, [props.buildingType, props.dataType, props.refreshKey])


  useEffect(() => {
    fetchPdfUrls()
  }, [props.buildingType, props.dataType])

  useEffect(() => {
    if (modalVisible && currentItem && pdfForm) {
      const existingUrl = pdfUrls[currentItem.id]
      pdfForm.setFieldsValue({
        type: existingUrl ? 'url' : 'url',
        url: existingUrl || ''
      })
    }
  }, [modalVisible, currentItem, pdfUrls])

  useEffect(() => {
    setColumns(generateTableColumns(initialColumns, false, (value, key, item) => {
      if (key === 'description') {
        const pdfUrl = pdfUrls[item.id]

        const openModal = (item) => {
          setCurrentItem(item)
          setModalVisible(true)
        }

        return (
          <div>
            <div>
              {pdfUrl ? (
                <a
                  href={pdfUrl.startsWith('http') ? pdfUrl : `https://${pdfUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                  {value}
                </a>
              ) : (
                <span>{value}</span>
              )}
            </div>
            {pdfUrl && (
              <Space size="small" style={{ marginTop: 4 }}>
                <Button size="small" type="default" onClick={() => openModal(item)}>
                  Edit PDF
                </Button>
                <Button size="small" type="default" danger onClick={() => deletePdfUrl(item.id)}>
                  Remove PDF
                </Button>
              </Space>
            )}
            {!pdfUrl && (
              <Button size="small" type="dashed" onClick={() => openModal(item)} style={{ marginTop: 4 }}>
                + Add PDF
              </Button>
            )}
          </div>
        )
      }

      // Editable price fields — only editable if hardcoded in JSON and the other price field isn't
      if (editableFields.includes(key)) {
        const rawItem = compareData.find(x => x.id === item.id)
        const isHardcoded = props.dataType === 'materials'
          ? isMaterialFieldEditable(rawItem, key)
          : rawItem && rawItem[key] !== null && rawItem[key] !== false && rawItem[key] !== undefined
        if (isHardcoded) {
          const draftItems = props.dataType === 'materials' ? props.draft?.materials : props.draft?.parts
          const draftEntry = draftItems?.find(d => (props.dataType === 'materials' ? d.materialId : d.partId) === item.id)
          const draftValue = draftEntry?.[key] ?? null
          const publishedValue = rawItem?.[key] ?? null
          return (
            <EditableCell
              value={value}
              draftValue={draftValue}
              publishedValue={publishedValue}
              field={key}
              item={item}
              buildingType={props.buildingType}
              dataType={props.dataType}
              onSaved={() => { refreshData(); props.onDraftChange && props.onDraftChange() }}
            />
          )
        }
        // calculated — fall through to normal rendering + blue italic style below
      }

      if (typeof value === 'number') {
        if (['pricePerPound', 'pricePerUnit', 'fastener', 'cover', 'galv', 'steelDrop', 'steelCost', 'labor', 'totalMrk'].includes(key)) {
          value = value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        } else if (['poundsPerUnit', 'poundsPerStock', 'weight'].includes(key)) {
          value = +value.toFixed(2) + ' lbs'
        }
      }
      if (value === null) value = 'null'
      if (value === undefined) value = 'undefined'
      if (value === false) value = 'false'
      if (value === true) value = 'true'
      if (Array.isArray(value)) value = value.join(', ')
      return value
    }, (value, key, item) => {
      if (key === 'pdfUrl') return {}

      // Check if this cell is editable
      if (editableFields.includes(key)) {
        const rawItem = compareData.find(x => x.id === item.id)
        const isHardcoded = props.dataType === 'materials'
          ? isMaterialFieldEditable(rawItem, key)
          : rawItem && rawItem[key] !== null && rawItem[key] !== false && rawItem[key] !== undefined
        if (isHardcoded) return { className: 'editable-cell' }
      }

      if ([null, undefined, false, true].includes(value)) {
        return { style: { color: '#C0C0C0' } }
      }

      const compareItem = compareData.find(x => x.id === item.id)
      if (value !== compareItem[key]) {
        return { style: { color: '#1890ff', fontWeight: 400 } }
      }
    }))
  }, [pdfUrls, modalVisible, initialColumns, data, props.draft])

  const fetchPdfUrls = async () => {
    try {
      const response = await axios.get('/api/parts-materials/urls', {
        params: {
          buildingType: props.buildingType,
          dataType: props.dataType
        },
        withCredentials: true
      })
      const urlMap = {}
      response.data.forEach(item => {
        urlMap[item.itemId] = item.pdfUrl
      })
      setPdfUrls(urlMap)
    } catch (error) {
      console.error('Error fetching PDFs:', error)
    }
  }

  const savePdf = async () => {
    if (!currentItem) return

    setConfirmLoading(true)

    try {
      const formData = pdfForm.getFieldsValue(true)

      if (formData.type === 'url') {
        await axios.post('/api/parts-materials/url', {
          itemId: currentItem.id,
          buildingType: props.buildingType,
          dataType: props.dataType,
          partNumber: currentItem.partNumber,
          pdfUrl: formData.url
        }, {
          withCredentials: true
        })

        setPdfUrls(prev => ({ ...prev, [currentItem.id]: formData.url }))
        message.success('PDF saved successfully')
      } else if (formData.type === 'file' && formData.file) {
        message.error('Please use the file upload button')
      }

      pdfForm.resetFields()
      setModalVisible(false)
      setCurrentItem(null)
      setConfirmLoading(false)
    } catch (error) {
      console.error('Error saving PDF:', error)
      message.error('Failed to save PDF')
      setConfirmLoading(false)
    }
  }

  const deletePdfUrl = async (itemId) => {
    try {
      await axios.delete('/api/parts-materials/url', {
        data: {
          itemId,
          buildingType: props.buildingType,
          dataType: props.dataType
        },
        withCredentials: true
      })

      setPdfUrls(prev => {
        const newUrls = { ...prev }
        delete newUrls[itemId]
        return newUrls
      })
      message.success('PDF deleted successfully')
    } catch (error) {
      console.error('Error deleting PDF:', error)
      message.error('Failed to delete PDF')
    }
  }

  const [columns, setColumns] = useState([])

  const [partsMaterialsColumns] = useState(generateTableColumns(initialPartsMaterialsColumns, false, (value) => {
    if (!Array.isArray(value)) value = [value]

    value = value.map(val => {
      if (Array.isArray(val)) {
        return '(' + val.join(' + ') + ')'
      } else if (typeof val === 'object' && val !== null) {
        let trueMultiplier = '(false)'
        let falseMultiplier = '(false)'

        if (Object.prototype.hasOwnProperty.call(val, 'trueMultiplier')) {
          trueMultiplier = val.trueMultiplier
        } else if (Object.prototype.hasOwnProperty.call(val, 'trueValue')) {
          trueMultiplier = val.trueValue + ' (VALUE)'
        }

        if (Object.prototype.hasOwnProperty.call(val, 'falseMultiplier')) {
          falseMultiplier = val.falseMultiplier
        } else if (Object.prototype.hasOwnProperty.call(val, 'falseValue')) {
          falseMultiplier = val.falseValue + ' (VALUE)'
        }

        return '(' + val.field + ' === ' + val.fieldValue + ' ? ' + trueMultiplier + ' : ' + falseMultiplier + ')'
      } else {
        if (val === null) val = 'null'
        if (val === undefined) val = 'undefined'
        if (val === false) val = 'false'
        if (val === true) val = 'true'

        return val
      }
    }).join(', ')

    return value
  }, (value) => {
    const falsies = [null, undefined, false, true]

    if (falsies.includes(value) || (Array.isArray(value) && value.some(v => falsies.includes(v)))) {
      return { color: '#C0C0C0' }
    }
  }))

  let missingPartNumbers = []
  let missingEquations = []
  let missingManufacturedPartNumbers = []
  let missingPartManufacturedParts = []
  let missingPartNumbersCount = 0
  let missingEquationsCount = 0
  let missingManufacturedPartNumbersCount = 0
  let missingPartManufacturedPartsCount = 0

  if (props.dataType === 'parts') {
    let manufacturedPartsCalculations = []
    let manufacturedPartsDataset = []

    missingPartNumbers = data.filter(item => !item.partNumber).map(item => item.id)
    missingPartNumbersCount = missingPartNumbers.length

    if (props.buildingType === 'integrity') {
      const integrityManufacturedPartsCalculations = Object.keys(getManufacturedPartCalculations('integrity'))
      const integrityCattleManufacturedPartsCalculations = Object.keys(getManufacturedPartCalculations('integrity-cattle'))
      const integrityManufacturedPartsDataset = getManufacturedPartsDataset('integrity')
      const integrityCattleManufacturedPartsDataset = getManufacturedPartsDataset('integrity-cattle')

      manufacturedPartsCalculations = [...integrityManufacturedPartsCalculations, ...integrityCattleManufacturedPartsCalculations]
      manufacturedPartsDataset = [...integrityManufacturedPartsDataset, ...integrityCattleManufacturedPartsDataset]
    } else {
      manufacturedPartsCalculations = Object.keys(getManufacturedPartCalculations(props.buildingType))
      manufacturedPartsDataset = getManufacturedPartsDataset(props.buildingType)
    }

    missingPartManufacturedParts = data.filter(item => item.partNumber && !manufacturedPartsDataset.map(i => i.partNumber).includes(item.partNumber)).map(item => item.id)
    missingPartManufacturedPartsCount = missingPartManufacturedParts.length
    missingEquations = data.filter(item => !manufacturedPartsCalculations.includes(item.partNumber) && !missingPartManufacturedParts.includes(item.id) && !missingPartNumbers.includes(item.id)).map(item => item.id)
    missingEquationsCount = missingEquations.length
    missingManufacturedPartNumbers = manufacturedPartsDataset.filter(item => !item.partNumber).map(item => item.id)
    missingManufacturedPartNumbersCount = missingManufacturedPartNumbers.length
  }

  return (
    <>
      <h2>{ convertStringToTitleCase(`${props.buildingType} ${props.dataType}`) }</h2>

      { props.dataType === 'parts' &&
        <div>
          <p>Missing Part Numbers: { missingPartNumbersCount }</p>
          <p>Missing Manufactured Part Numbers: { missingManufacturedPartNumbersCount }</p>
          <p>Missing Manufactured Part for Parts: { missingPartManufacturedPartsCount }</p>
          <p>Missing Quantity Equations: { missingEquationsCount }</p>
        </div>
      }

      <Space style={{ marginBottom: 8 }}>
        <Search
          placeholder='Search...'
          onSearch={ input => handleSearch(input, data, setData) }
          onChange={ e => handleSearch(e.target.value, data, setData) }
          style={{ width: 300 }}
          allowClear />
        {/* {props.dataType === 'materials' && (
          <Button onClick={() => setBulkUpdateOpen(true)}>Bulk Update Price / lb</Button>
        )} */}
      </Space>

      <BulkUpdateModal
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        buildingType={props.buildingType}
        compareData={compareData}
        liveData={data}
        onSaved={() => { refreshData(); props.onDraftChange && props.onDraftChange() }}
      />

      <Table
        columns={ columns }
        dataSource={ data }
        loading={ tableLoading }
        locale={{ emptyText: tableLoading ? ' ' : 'No data' }}
        expandable={{ expandedRowRender: (item) => {
          if (item.material && Array.isArray(item.material)) {
            return (
              <>
                <h3>material</h3>
                <Table
                  columns={ partsMaterialsColumns }
                  dataSource={ item.material }
                  pagination={ false }
                  rowKey='id'
                  size='small' />
              </>
            )
          }
        }}}
        pagination={{ pageSize: 50 }}
        rowClassName={ (item) => {
          const draftItems = props.dataType === 'materials' ? props.draft?.materials : props.draft?.parts
          const inDraft = draftItems?.some(d => (props.dataType === 'materials' ? d.materialId : d.partId) === item.id)
          const noExpand = !item.material || !item.material.length
          return [inDraft ? 'row-draft' : null, noExpand ? 'no-expand' : null].filter(Boolean).join(' ') || null
        }}
        onRow={ (item) => {
          if (props.dataType === 'parts') {
            if (!item.partNumber) {
              return { style: { backgroundColor: '#FFD580' } }
            } else if (missingPartManufacturedParts.includes(item.id)) {
              return { style: { backgroundColor: '#DDD5F3' } }
            } else if (missingEquations.includes(item.id)) {
              return { style: { backgroundColor: '#FFCCCB' } }
            }
          }
        }}
        rowKey='id'
        scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
        size='small' />

      <div style={{ width: '50%' }}>
        <p><b>Color Codes</b></p>
        <p>Hard-coded value</p>
        <p style={{ color: '#C0C0C0' }}>Programmical value</p>
        <p style={{ color: '#1890ff', fontWeight: 400 }}>Value generated by calculator</p>
        <p style={{ backgroundColor: '#FFD580' }}>Part doesn't have a part number</p>
        <p style={{ backgroundColor: '#DDD5F3' }}>Part doesn't have a manufactured part associated with it</p>
        <p style={{ backgroundColor: '#FFCCCB' }}>Part's associated manufactured part does not have a quantity equation</p>
      </div>

      <Modal
        title={pdfUrls[currentItem?.id] ? 'Edit PDF' : 'Add PDF'}
        open={modalVisible}
        onOk={savePdf}
        onCancel={() => {
          pdfForm.resetFields()
          setModalVisible(false)
          setCurrentItem(null)
        }}
        confirmLoading={confirmLoading}
        okText="Save Changes"
        width={600}
      >
        {currentItem && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 4 }}>{currentItem.description}</h4>
            {currentItem.partNumber && (
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                Part Number: {currentItem.partNumber}
              </p>
            )}
          </div>
        )}

        <Form form={pdfForm} initialValues={{ type: 'url' }}>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="url">URL Link</Radio>
              <Radio value="file">Upload File</Radio>
            </Radio.Group>
          </Form.Item>

          {pdfType === 'url' && (
            <Form.Item
              name="url"
              label="PDF URL"
              rules={[{ required: true, message: 'Please enter a PDF URL' }]}
            >
              <Input placeholder="https://example.com/document.pdf" />
            </Form.Item>
          )}

          {pdfType === 'file' && (
            <>
              {pdfUrls[currentItem?.id] && (
                <Form.Item label="Current File">
                  <a
                    href={pdfUrls[currentItem.id].startsWith('http') ? pdfUrls[currentItem.id] : `https://${pdfUrls[currentItem.id]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Current PDF
                  </a>
                </Form.Item>
              )}
              <Form.Item
                name="file"
                label={pdfUrls[currentItem?.id] ? 'Replace File' : 'PDF File'}
                valuePropName="file"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!pdfUrls[currentItem?.id] && !value) {
                        return Promise.reject(new Error('PDF file is required'))
                      }
                      return Promise.resolve()
                    }
                  })
                ]}
              >
                <Upload
                  name="file"
                  action={`${axios.defaults.baseURL || ''}/api/parts-materials/upload`}
                  data={{
                    itemId: currentItem?.id,
                    buildingType: props.buildingType,
                    dataType: props.dataType,
                    partNumber: currentItem?.partNumber
                  }}
                  accept=".pdf"
                  showUploadList={false}
                  withCredentials={true}
                  onChange={(info) => {
                    if (info.file.status === 'done') {
                      const response = info.file.response
                      setPdfUrls(prev => ({ ...prev, [currentItem.id]: response.pdfUrl }))
                      pdfForm.resetFields()
                      setModalVisible(false)
                      setCurrentItem(null)
                      message.success('PDF uploaded successfully')
                    } else if (info.file.status === 'error') {
                      message.error('PDF upload failed')
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    {pdfUrls[currentItem?.id] ? 'Choose New PDF File' : 'Choose PDF File'}
                  </Button>
                </Upload>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  )
}
